import { Col, Row } from 'antd';
import { useSearchParamsContext } from 'contexts/SearchParamsContext';
import { useCallback } from 'react';
import { Form as FormRFF, Field as FieldRFF } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { DocumentModel } from 'api/documents/documents.model';
import Button from 'components/Buttons';
import Form from 'components/Form';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import { CertificateDetailsModel } from 'models/certificate.model';
import { CATEGORY_TYPE } from 'models/documents.model';

import { FilterableQueryRefetchType } from '../../../types';

interface CertificatesFiltersProps
  extends FilterableQueryRefetchType<DocumentModel<CertificateDetailsModel>> {
  categories?: CATEGORY_TYPE[];
}

interface CertificatesFiltersInterface {
  categoryName: number;
  productId: string;
}

const CertificatesFilters = ({ isFetching, categories = [] }: CertificatesFiltersProps) => {
  const { t } = useTranslation();

  const { searchParams, updateSearchParams, clearSearchParams } = useSearchParamsContext();

  const onSubmit = ({ categoryName, productId = '' }: CertificatesFiltersInterface) => {
    updateSearchParams({ page: 1, categoryName, productId });
  };
  const onClearCategory = useCallback(
    () => updateSearchParams({ ...searchParams, page: 1, categoryName: '' }),
    [searchParams, updateSearchParams]
  );

  return (
    <FormRFF<CertificatesFiltersInterface>
      onSubmit={onSubmit}
      initialValues={{
        categoryName: searchParams.categoryName || '',
        productId: searchParams.productId || '',
      }}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit} data-cy="form">
          <Row gutter={30} justify="end">
            <Col md={24} lg={12}>
              <FieldRFF
                name="categoryName"
                label={t('certificate.typeOfCertificate')}
                data-cy="form_categoryName"
                component={Select}
                options={categories.map((type) => ({
                  value: type,
                  label: t(`certificate.${type === CATEGORY_TYPE.CERTIFICATE ? 'other' : type}`),
                }))}
                size="large"
                allowClear
                disabled={isFetching}
                onClear={onClearCategory}
              />
            </Col>
            <Col md={24} lg={12}>
              <FieldRFF
                name="productId"
                label={t('certificate.certificateUID')}
                data-cy="form_productId"
                component={Input}
                size="large"
                disabled={isFetching}
              />
            </Col>
          </Row>
          <Row gutter={[30, 10]} justify="end">
            <Col md={24} lg={4}>
              <Button
                data-cy="form_clear"
                $block
                loading={isFetching}
                disabled={isFetching}
                onClick={clearSearchParams}
              >
                {t('clearAll')}
              </Button>
            </Col>
            <Col md={24} lg={4}>
              <Button
                type="primary"
                htmlType="submit"
                data-cy="form_submit"
                $block
                loading={isFetching}
                disabled={isFetching}
              >
                {t('search2')}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </FormRFF>
  );
};

export default CertificatesFilters;
